import React, { useState } from 'react';
import { Copy, Users, TrendingUp, DollarSign, Calendar, CheckCircle, Gift, Share2, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useReferrals } from '../hooks/useReferrals';
import { useTranslation } from 'react-i18next';
import { formatReferralCode } from '../utils/formatReferralCode';
import { Editorial, Typography, BorderRadius, NavyPalette } from '../styles/primitives';

const ReferralTab: React.FC = () => {
  const { t } = useTranslation();
  const {
    loading,
    referralCode,
    referralEarnings,
    referralStats,
    referredUsers,
    copyReferralLink,
    copyReferralCode,
  } = useReferrals();

  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const handleCopyCode = () => {
    copyReferralCode();
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleCopyLink = () => {
    copyReferralLink();
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const referralLink = referralCode ? `${window.location.origin}/signup?ref=${referralCode}` : '';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-xl font-bold text-black mb-2">Loading Referral Data</h2>
          <p className="text-slate-600">Please wait while we fetch your referral information</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-8 bg-slate-50/95">
      <div
        className="backdrop-blur-sm rounded-2xl p-8 border"
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(147, 51, 234, 0.15))',
              border: '2px solid rgba(59, 130, 246, 0.2)'
            }}
          >
            <Gift size={24} style={{ color: '#3b82f6' }} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-black">Referral Program</h2>
            <p className="text-slate-700 text-sm">
              {(() => {
                const count = referralStats.totalReferrals;
                let rate = '1%';
                let nextTier = '';

                if (count >= 100) {
                  rate = '5%';
                  nextTier = 'Maximum tier reached!';
                } else if (count >= 50) {
                  rate = '4%';
                  nextTier = `${100 - count} more to reach 5%`;
                } else if (count >= 25) {
                  rate = '3%';
                  nextTier = `${50 - count} more to reach 4%`;
                } else if (count >= 10) {
                  rate = '2%';
                  nextTier = `${25 - count} more to reach 3%`;
                } else if (count >= 3) {
                  rate = '1.5%';
                  nextTier = `${10 - count} more to reach 2%`;
                } else {
                  rate = '1%';
                  nextTier = `${3 - count} more to reach 1.5%`;
                }

                return (
                  <>
                    Current tier: <span className="font-bold text-emerald-500">{rate}</span> commission
                    {count < 100 && <span className="text-slate-600"> • {nextTier}</span>}
                  </>
                );
              })()}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 mb-6 border" style={{ borderColor: 'rgba(0, 0, 0, 0.06)' }}>
          <div className="text-slate-600 text-sm mb-2">Your Referral Code</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white text-black px-4 py-3 rounded-lg font-mono text-lg tracking-widest border border-slate-300">
              {referralCode ? formatReferralCode(referralCode) : ""}
            </code>
            <button
              onClick={handleCopyCode}
              className="text-white p-3 rounded-lg transition-all flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {copiedCode ? <CheckCircle size={20} /> : <Copy size={20} />}
              {copiedCode && <span className="text-sm">Copied!</span>}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border" style={{ borderColor: 'rgba(0, 0, 0, 0.06)' }}>
          <div className="text-slate-600 text-sm mb-2">Your Referral Link</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-white text-black px-4 py-3 rounded-lg font-mono text-sm overflow-hidden text-ellipsis whitespace-nowrap border border-slate-300">
              {referralLink || 'Loading...'}
            </div>
            <button
              onClick={handleCopyLink}
              className="text-white p-3 rounded-lg transition-all flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.4)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {copiedLink ? <CheckCircle size={20} /> : <Share2 size={20} />}
              {copiedLink && <span className="text-sm">Copied!</span>}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          className="backdrop-blur-sm rounded-xl p-6 border"
          style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderColor: 'rgba(0, 0, 0, 0.06)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
          }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center" style={{ border: '2px solid rgba(59, 130, 246, 0.3)' }}>
              <Users size={20} style={{ color: '#3b82f6' }} />
            </div>
            <div className="text-slate-600 text-sm">Total Referrals</div>
          </div>
          <div className="text-3xl font-bold text-black">{referralStats.totalReferrals}</div>
          <div className="text-emerald-500 text-sm mt-1">
            {referralStats.activeReferrals} active this month
          </div>
        </div>

        <div
          className="backdrop-blur-sm rounded-xl p-6 border"
          style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderColor: 'rgba(0, 0, 0, 0.06)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
          }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center" style={{ border: '2px solid rgba(16, 185, 129, 0.3)' }}>
              <DollarSign size={20} style={{ color: '#10b981' }} />
            </div>
            <div className="text-slate-600 text-sm">Total Earnings</div>
          </div>
          <div className="text-3xl font-bold text-black">
            ${referralStats.totalEarnings.toFixed(2)}
          </div>
          <div className="text-slate-600 text-sm mt-1">Lifetime commission</div>
        </div>

        <div
          className="backdrop-blur-sm rounded-xl p-6 border"
          style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderColor: 'rgba(0, 0, 0, 0.06)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
          }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center" style={{ border: '2px solid rgba(168, 85, 247, 0.3)' }}>
              <TrendingUp size={20} style={{ color: '#a855f7' }} />
            </div>
            <div className="text-slate-600 text-sm">This Month</div>
          </div>
          <div className="text-3xl font-bold text-black">
            ${referralStats.earningsThisMonth.toFixed(2)}
          </div>
          <div className="text-slate-600 text-sm mt-1">Current month earnings</div>
        </div>

        <div
          className="backdrop-blur-sm rounded-xl p-6 border"
          style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderColor: 'rgba(0, 0, 0, 0.06)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
          }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center" style={{ border: '2px solid rgba(245, 158, 11, 0.3)' }}>
              <Calendar size={20} style={{ color: '#f59e0b' }} />
            </div>
            <div className="text-slate-600 text-sm">Today</div>
          </div>
          <div className="text-3xl font-bold text-black">
            ${referralStats.earningsToday.toFixed(2)}
          </div>
          <div className="text-slate-600 text-sm mt-1">Today's earnings</div>
        </div>
      </div>

      <div
        className="backdrop-blur-sm rounded-2xl p-6 border"
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}
      >
        <h3 className="text-xl font-semibold text-black mb-4 flex items-center gap-2">
          <Users size={20} style={{ color: '#3b82f6' }} />
          Your Referrals
        </h3>

        {referredUsers.length === 0 ? (
          <div className="text-center py-8">
            <div
              className="w-16 h-16 flex items-center justify-center mx-auto mb-4"
              style={{
                background: 'rgba(0, 0, 0, 0.04)',
                borderRadius: '12px',
              }}
            >
              <Users
                size={32}
                style={{
                  color: 'rgba(0, 0, 0, 0.4)',
                }}
              />
            </div>
            <p
              className="mb-2"
              style={{
                color: '#000000',
                fontSize: Typography.BodyLarge,
                fontWeight: Typography.WeightMedium,
                lineHeight: Typography.LeadingNormal,
              }}
            >
              No referrals yet
            </p>
            <p
              style={{
                color: 'rgba(0, 0, 0, 0.6)',
                fontSize: Typography.BodySmall,
                lineHeight: Typography.LeadingNormal,
              }}
            >
              Share your referral link to start earning commissions
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}>
                  <th className="text-left text-slate-600 text-sm font-medium py-3 px-2">User</th>
                  <th className="text-left text-slate-600 text-sm font-medium py-3 px-2">Joined</th>
                  <th className="text-right text-slate-600 text-sm font-medium py-3 px-2">Total Earned</th>
                  <th className="text-right text-slate-600 text-sm font-medium py-3 px-2">Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {referredUsers.map((referredUser) => (
                  <tr key={referredUser.id} className="border-b transition-colors" style={{ borderColor: 'rgba(0, 0, 0, 0.06)' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.02)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                    <td className="py-3 px-2">
                      <div className="text-black font-medium">{referredUser.email}</div>
                    </td>
                    <td className="py-3 px-2 text-slate-600 text-sm">
                      {new Date(referredUser.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="text-emerald-500 font-semibold">
                        ${referredUser.total_earned_from_user.toFixed(2)}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right text-slate-600 text-sm">
                      {referredUser.last_earning_date
                        ? new Date(referredUser.last_earning_date).toLocaleDateString()
                        : 'No activity'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div
        className="backdrop-blur-sm rounded-2xl p-6 border"
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}
      >
        <h3 className="text-xl font-semibold text-black mb-4 flex items-center gap-2">
          <TrendingUp size={20} style={{ color: '#10b981' }} />
          Recent Commission Earnings
        </h3>

        {referralEarnings.length === 0 ? (
          <div className="text-center py-8">
            <div
              className="w-16 h-16 flex items-center justify-center mx-auto mb-4"
              style={{
                background: 'rgba(0, 0, 0, 0.04)',
                borderRadius: '12px',
              }}
            >
              <DollarSign
                size={32}
                style={{
                  color: 'rgba(0, 0, 0, 0.4)',
                }}
              />
            </div>
            <p
              className="mb-2"
              style={{
                color: '#000000',
                fontSize: Typography.BodyLarge,
                fontWeight: Typography.WeightMedium,
                lineHeight: Typography.LeadingNormal,
              }}
            >
              No earnings yet
            </p>
            <p
              style={{
                color: 'rgba(0, 0, 0, 0.6)',
                fontSize: Typography.BodySmall,
                lineHeight: Typography.LeadingNormal,
              }}
            >
              You'll earn 1% commission when your referrals close winning positions
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3 mb-4">
              {referralEarnings
                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .map((earning) => (
                  <div
                    key={earning.id}
                    className="rounded-lg p-4 border transition-all"
                    style={{
                      background: '#ffffff',
                      borderColor: 'rgba(0, 0, 0, 0.06)',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.06)';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.02)';
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center" style={{ border: '2px solid rgba(16, 185, 129, 0.3)' }}>
                          <DollarSign size={20} style={{ color: '#10b981' }} />
                        </div>
                        <div>
                          <div className="text-black font-semibold">
                            +${earning.commission_amount.toFixed(2)}
                          </div>
                          <div className="text-slate-600 text-sm">
                            {earning.referred_user_email || 'User'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-black font-medium">
                          {earning.position_symbol} {earning.position_side}
                        </div>
                        <div className="text-slate-600 text-sm">
                          PnL: ${earning.position_pnl.toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <div className="text-slate-600 text-xs">
                      {new Date(earning.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
            </div>

            {referralEarnings.length > itemsPerPage && (
              <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}>
                <div className="text-slate-600 text-sm">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, referralEarnings.length)} of{' '}
                  {referralEarnings.length} earnings
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="flex items-center gap-1 px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-all"
                    style={currentPage !== 1 ? {
                      background: 'linear-gradient(135deg, #d91222 0%, #b81018 100%)',
                      boxShadow: '0 2px 8px rgba(217, 18, 34, 0.3)'
                    } : {
                      background: 'rgba(0, 0, 0, 0.1)',
                      color: 'rgba(0, 0, 0, 0.4)'
                    }}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>
                  <div className="text-slate-600 text-sm px-3">
                    Page {currentPage} of {Math.ceil(referralEarnings.length / itemsPerPage)}
                  </div>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(referralEarnings.length / itemsPerPage)))}
                    disabled={currentPage === Math.ceil(referralEarnings.length / itemsPerPage)}
                    className="flex items-center gap-1 px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-all"
                    style={currentPage !== Math.ceil(referralEarnings.length / itemsPerPage) ? {
                      background: 'linear-gradient(135deg, #d91222 0%, #b81018 100%)',
                      boxShadow: '0 2px 8px rgba(217, 18, 34, 0.3)'
                    } : {
                      background: 'rgba(0, 0, 0, 0.1)',
                      color: 'rgba(0, 0, 0, 0.4)'
                    }}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div
        className="backdrop-blur-sm rounded-2xl p-6 border"
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}
      >
        <h3 className="text-lg font-semibold text-black mb-4">Commission Tiers</h3>
        <div className="space-y-3 mb-6">
          <div className="flex items-center justify-between bg-white rounded-lg p-3 border" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <span className="text-slate-600 font-bold text-sm">0-2</span>
              </div>
              <div className="text-slate-700">0-2 referrals</div>
            </div>
            <div className="text-black font-bold">1%</div>
          </div>
          <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-500/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <span className="text-blue-500 font-bold text-sm">3-9</span>
              </div>
              <div className="text-blue-600">3-9 referrals</div>
            </div>
            <div className="text-blue-500 font-bold">1.5%</div>
          </div>
          <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-cyan-500/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <span className="text-cyan-500 font-bold text-sm">10-24</span>
              </div>
              <div className="text-cyan-600">10-24 referrals</div>
            </div>
            <div className="text-cyan-500 font-bold">2%</div>
          </div>
          <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-emerald-500/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <span className="text-emerald-500 font-bold text-sm">25-49</span>
              </div>
              <div className="text-emerald-600">25-49 referrals</div>
            </div>
            <div className="text-emerald-500 font-bold">3%</div>
          </div>
          <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-500/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <span className="text-amber-500 font-bold text-sm">50-99</span>
              </div>
              <div className="text-amber-600">50-99 referrals</div>
            </div>
            <div className="text-amber-500 font-bold">4%</div>
          </div>
          <div
            className="flex items-center justify-between rounded-lg p-3 border"
            style={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(168, 85, 247, 0.1))',
              borderColor: 'rgba(139, 92, 246, 0.3)',
              boxShadow: '0 2px 8px rgba(139, 92, 246, 0.2)'
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{
                  background: 'rgba(139, 92, 246, 0.2)',
                  border: '2px solid rgba(139, 92, 246, 0.3)'
                }}
              >
                <span
                  className="font-bold text-sm"
                  style={{ color: '#8b5cf6' }}
                >
                  100+
                </span>
              </div>
              <div
                className="font-semibold"
                style={{ color: '#8b5cf6' }}
              >
                100+ referrals
              </div>
            </div>
            <div
              className="font-bold text-lg"
              style={{ color: '#8b5cf6' }}
            >
              5%
            </div>
          </div>
        </div>
        <div className="border rounded-lg p-4" style={{ background: 'rgba(59, 130, 246, 0.08)', borderColor: 'rgba(59, 130, 246, 0.2)' }}>
          <div className="flex items-start gap-3">
            <TrendingUp size={20} style={{ color: '#3b82f6' }} className="flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700">
              <span className="font-semibold text-black">Unlock higher earnings!</span> Your commission rate automatically increases as you refer more friends. Start at 1% and work your way up to 5% by building a strong referral network.
            </div>
          </div>
        </div>
      </div>

      <div
        className="backdrop-blur-sm rounded-2xl p-6 border"
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}
      >
        <h3 className="text-lg font-semibold text-black mb-4">How It Works</h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ border: '2px solid rgba(59, 130, 246, 0.3)' }}>
              <span className="text-blue-500 font-bold">1</span>
            </div>
            <div>
              <div className="text-black font-medium">Share Your Link</div>
              <div className="text-slate-600 text-sm">
                Copy your unique referral link and share it with friends
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ border: '2px solid rgba(168, 85, 247, 0.3)' }}>
              <span className="text-purple-500 font-bold">2</span>
            </div>
            <div>
              <div className="text-black font-medium">They Sign Up & Trade</div>
              <div className="text-slate-600 text-sm">
                When they join using your link and start trading, you're connected
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ border: '2px solid rgba(16, 185, 129, 0.3)' }}>
              <span className="text-emerald-500 font-bold">3</span>
            </div>
            <div>
              <div className="text-black font-medium">Earn Commission</div>
              <div className="text-slate-600 text-sm">
                Every time they close a winning position, you automatically earn commission on their profit
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralTab;
